/**
 * «Простои»: акцепт и простои сотрудников подразделения за период. Спека
 * `docs/specs/2026-09-24-idle-period-design.md`; числа — те же, что у
 * `prokat-runner idle-period`, из одного модуля на сервере.
 */
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { type IdlePerson, type IdleReport, KC, lastWeek, loadIdle, pctClass, thisWeek } from "@/lib/idle";
import { workTime } from "@/lib/leads";

const dm = (day: string) => `${day.slice(8, 10)}.${day.slice(5, 7)}`;

/** Период и подразделение из адреса: ссылку можно переслать. */
export function useIdleParams() {
  const [params, setParams] = useSearchParams();
  const def = lastWeek();
  const p = {
    from: params.get("from") || def.from,
    to: params.get("to") || def.to,
    location: Number(params.get("location")) || KC,
  };
  const set = (next: Partial<typeof p>) => {
    const v = { ...p, ...next };
    setParams({ from: v.from, to: v.to, location: String(v.location) }, { replace: true });
  };
  return [p, set] as const;
}

export function PeriodBar({
  title,
  p,
  set,
  locations,
}: {
  title: ReactNode;
  p: { from: string; to: string; location: number };
  set: (next: Partial<{ from: string; to: string; location: number }>) => void;
  locations?: { id: number; name: string }[];
}) {
  const input = "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg";
  const btn = "h-8 rounded-[var(--radius-ui)] border border-border px-2.5 text-fg-muted hover:bg-bg-hover";
  return (
    <div className="flex flex-wrap items-center gap-3" data-annot="шапка простоев">
      <h1 className="text-[16px] font-semibold tracking-[-0.015em]">{title}</h1>
      {locations && (
        <select
          value={p.location}
          onChange={(e) => set({ location: Number(e.target.value) })}
          className={input}
          data-annot="выбор подразделения"
        >
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
      )}
      <div className="ml-auto flex flex-wrap items-center gap-1.5 text-[12.5px]" data-annot="выбор периода">
        <button type="button" className={btn} onClick={() => set(lastWeek())}>
          прошлая неделя
        </button>
        <button type="button" className={btn} onClick={() => set(thisWeek())}>
          эта неделя
        </button>
        <input type="date" value={p.from} onChange={(e) => e.target.value && set({ from: e.target.value })} className={input} />
        <span className="text-fg-faint">—</span>
        <input type="date" value={p.to} onChange={(e) => e.target.value && set({ to: e.target.value })} className={input} />
      </div>
    </div>
  );
}

function Tile({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div
      className="flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4"
      data-annot={`плитка «${label}»`}
    >
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className="text-[26px] font-semibold leading-none tracking-[-0.02em] tabular-nums">{value}</div>
      {hint && <div className="text-[12px] text-fg-faint">{hint}</div>}
    </div>
  );
}

export const pct = (v: number | null) => <span className={pctClass(v)}>{v === null ? "—" : `${v}%`}</span>;

/** Строка сотрудника и строка итога — общие поля. */
type Row = Omit<IdlePerson, "user_id" | "name" | "days">;
type Column = { head: string; hint: string; cell: (p: Row) => ReactNode; activity?: boolean };

const COLUMNS: Column[] = [
  { head: "Часы", hint: "оплачиваемые статусы: Работаю, Задача(В очереди), Задача(Без очереди)", cell: (p) => workTime(p.paid_min) },
  { head: "Акцепт", hint: "подтверждённое время (sUser_time_confirm) — за него платим", cell: (p) => workTime(p.accept_min) },
  {
    head: "Не акцепт.",
    hint: "часы по статусам сверх акцепта, по дням с акцептом",
    cell: (p) => (p.unaccepted_min ? workTime(p.unaccepted_min) : <span className="text-fg-faint">0</span>),
  },
  { head: "Простой", hint: "куски акцепта от 15 мин без звонка и без отправки в мессенджер", cell: (p) => workTime(p.idle_min), activity: true },
  { head: "% простоя", hint: "простой / акцепт; < 15% зелёный, < 30% жёлтый, иначе красный", cell: (p) => <b>{pct(p.idle_pct)}</b>, activity: true },
  { head: "Простоев", hint: "сколько кусков простоя", cell: (p) => p.idle_count ?? "—", activity: true },
  {
    head: "Пропущено",
    hint: "непринятые входящие, пришедшие в простое, когда сотрудник был на линии и свободен",
    cell: (p) => (p.idle_missed ? <span className="text-warn">{p.idle_missed}</span> : (p.idle_missed ?? "—")),
    activity: true,
  },
  {
    head: "Без акцепта",
    hint: "дней со статусами, но ещё без акцепта (подтверждают через 1–6 дней); в проценты не входят",
    cell: (p) => (p.pending_days ? <span className="text-warn">{p.pending_days} дн.</span> : <span className="text-fg-faint">0</span>),
  },
];

function Report({ r, location }: { r: IdleReport; location: number }) {
  const navigate = useNavigate();
  const t = r.totals;
  const cols = COLUMNS.filter((c) => r.activity || !c.activity);
  const cell = "whitespace-nowrap px-2.5 py-1.5 text-right tabular-nums";
  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Сотрудников" value={t.people} hint={`${dm(r.from)} — ${dm(r.to)}`} />
        <Tile label="Акцепт" value={workTime(t.accept_min)} hint={`по статусам ${workTime(t.paid_min)}`} />
        {r.activity && (
          <>
            <Tile label="Простой" value={workTime(t.idle_min)} hint={`${t.idle_count} кусков от ${r.idle_min_threshold} мин`} />
            <Tile label="% простоя" value={pct(t.idle_pct)} hint="простой / акцепт по дням с акцептом" />
          </>
        )}
      </div>
      {!r.activity && (
        <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4 text-[12.5px] text-fg-muted" data-annot="салон без активности">
          Активность «{r.location.name}» пока не считается: у салонов нет звонков и сообщений, а другие действия
          — второй этап. Здесь только часы и акцепт.
        </div>
      )}
      <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="сотрудники">
        {r.people.length === 0 ? (
          <div className="p-4 text-[12.5px] text-fg-muted">За этот период нет ни часов, ни акцепта.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="text-left text-[11.5px] text-fg-faint">
                <tr>
                  <th className="px-4 py-2 font-medium">Сотрудник</th>
                  {cols.map((c) => (
                    <th
                      key={c.head}
                      title={c.hint}
                      data-annot={`колонка «${c.head}»`}
                      className="cursor-help whitespace-nowrap px-2.5 py-2 text-right font-medium underline decoration-dotted underline-offset-2"
                    >
                      {c.head}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {r.people.map((p: IdlePerson) => (
                  <tr
                    key={p.user_id}
                    className="cursor-pointer border-t border-border hover:bg-bg-hover"
                    data-annot={`сотрудник ${p.user_id}`}
                    title="Открыть простои по дням"
                    onClick={() => navigate(`/idle/${p.user_id}?from=${r.from}&to=${r.to}&location=${location}`)}
                  >
                    <td className="whitespace-nowrap px-4 py-1.5">{p.name}</td>
                    {cols.map((c) => (
                      <td key={c.head} className={cell} data-annot={c.head}>
                        {c.cell(p)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t border-border-strong font-medium">
                <tr data-annot="итог">
                  <td className="px-4 py-2">Итого</td>
                  {cols.map((c) => (
                    <td key={c.head} className={cell}>
                      {c.cell(t)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

export function IdlePage() {
  const [p, set] = useIdleParams();
  const q = useQuery({ queryKey: ["idle", p.from, p.to, p.location], queryFn: () => loadIdle(p) });
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <PeriodBar
        title="Простои"
        p={p}
        set={set}
        locations={q.data?.locations ?? [{ id: KC, name: "Контакт Центр" }]}
      />
      {q.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Считаю акцепт и простои по CRM…</div>
      ) : q.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить простои: {String(q.error.message)}</div>
      ) : (
        <Report r={q.data} location={p.location} />
      )}
    </main>
  );
}
